import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Languages, MessageCircle } from "lucide-react";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL, WA_LINK } from "../lib/data";
import { useLang } from "../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export default function SeoPage({ page }) {
  const { lang, t, toggle } = useLang();
  const c = t.seo[page];
  const other = page === "lucky" ? "bestway" : "lucky";
  const otherLink = t.seoLinks.find((l) => l.href.includes(other));

  useEffect(() => {
    document.title = c.metaTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", c.metaDesc);
    window.scrollTo(0, 0);
  }, [c]);

  return (
    <div className="min-h-screen bg-ink text-cream font-body" data-testid={`seo-page-${page}`}>
      <header className="fixed top-0 inset-x-0 z-50 py-4 bg-ink/70 backdrop-blur-xl border-b border-cream/5">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="seo-nav-logo">
            <img
              src="/assets/gt-logo.png"
              alt="Ghauri Traders logo"
              className="w-9 h-9 rounded-xl object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <span className="font-display tracking-tight font-bold">
              Ghauri <span className="text-brass">Traders</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={toggle}
              data-testid="lang-toggle-button"
              aria-label={lang === "en" ? "Switch to Urdu" : "Switch to English"}
              className="inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-4 py-2 font-display text-xs font-bold text-cream/85 transition-colors duration-300 hover:border-brass/50 hover:text-brasslight"
            >
              <Languages size={14} strokeWidth={2.4} />
              {lang === "en" ? "اردو" : "EN"}
            </button>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="seo-order-now-button"
              className="inline-flex items-center gap-1.5 rounded-full bg-brass px-5 py-2.5 font-display text-sm font-bold text-ink transition-all duration-300 hover:bg-brasslight active:scale-95"
            >
              {t.navOrder} <ArrowUpRight size={15} strokeWidth={2.5} className="rtl:-scale-x-100" />
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 pt-36 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-cream/20 bg-cream/5 px-4 py-2 text-xs font-bold text-brasslight">
            <span className="w-1.5 h-1.5 rounded-full bg-brass" />
            {c.eyebrow}
          </span>
          <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08]">
            {c.h1Pre}{" "}
            <em className="font-serif italic font-medium text-brasslight">{c.h1Accent}</em>
          </h1>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="mt-8 space-y-5 max-w-3xl text-base md:text-lg leading-relaxed text-cream/70"
        >
          {c.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.25, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`seo-whatsapp-cta-${page}`}
            className="pulse-ring inline-flex items-center gap-2.5 rounded-full bg-brass px-8 py-4 font-display text-base font-bold text-ink transition-all duration-300 hover:bg-brasslight active:scale-95"
          >
            <MessageCircle size={19} strokeWidth={2.4} />
            {c.cta}
          </a>
          <a
            href={c.reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`seo-report-link-${page}`}
            className="inline-flex items-center gap-1.5 font-display text-sm font-bold text-brasslight hover:text-cream transition-colors"
          >
            {t.viewLabReport}
          </a>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {c.cards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 + i * 0.1, ease: EASE }}
              className="rounded-3xl bg-espresso border border-cream/5 p-7"
              data-testid={`seo-card-${page}-${i}`}
            >
              <h2 className="font-display text-lg font-bold tracking-tight text-brasslight">{card.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-cream/70">{card.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.55 }}
          className="mt-14 rounded-[2rem] bg-cream text-ink p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6"
        >
          <div>
            <p className="font-display text-xl font-bold tracking-tight">{t.contactEyebrow}</p>
            <p className="mt-2 text-sm text-ink/70" dir="ltr">
              {PHONE_DISPLAY} · {EMAIL}
            </p>
          </div>
          <a
            href={PHONE_TEL}
            data-testid={`seo-call-link-${page}`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-7 py-3.5 font-display text-sm font-bold text-brasslight transition-all duration-300 hover:bg-espresso active:scale-95"
          >
            {PHONE_DISPLAY}
          </a>
        </motion.div>

        <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm font-semibold">
          <Link to="/" data-testid="seo-back-home-link" className="text-brasslight hover:text-cream transition-colors">
            {t.backHome}
          </Link>
          {otherLink && (
            <Link
              to={otherLink.href}
              data-testid={`seo-cross-link-${other}`}
              className="text-taupe hover:text-brasslight transition-colors"
            >
              {otherLink.label}
            </Link>
          )}
        </div>
      </main>

      <footer className="border-t border-cream/10 py-7">
        <p className="max-w-5xl mx-auto px-5 sm:px-8 text-xs text-taupe">{t.footer1}</p>
      </footer>
    </div>
  );
}
